import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import type { VideoURLValueType } from '@elfsight/configurator-contracts';
import { useDebounceCallback } from '../shared';

export interface BackgroundVideoProps {
  type: VideoURLValueType;
  id?: string;
  originalValue?: string;
  scale?: number;
  offsetTop?: number;
  fixOnScroll?: boolean;
}

export function BackgroundVideo({
  type,
  id,
  originalValue,
  scale = 1,
  offsetTop = 0,
  fixOnScroll,
  ...forwardingProps
}: BackgroundVideoProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialContainerTopPosition = useRef(
    containerRef.current?.getBoundingClientRect().top || 0
  );

  const isIframe = ['vimeo', 'youtube'].includes(type);
  const isVideo = type === 'url';

  const iframeURL = useMemo(() => {
    if (!id || isVideo) {
      return;
    }

    return {
      youtube: getYoutubeURL(id),
      vimeo: getVimeoURL(id)
    }[type];
  }, [type, id]);

  const [scrollPosition, setScrollPosition] = useState(
    offsetTop < 0 ? -offsetTop : 0
  );

  const transform = `translate3d(0, ${
    fixOnScroll ? scrollPosition : 0
  }px, 0) scale(${2 * scale})`;

  const handleScroll = useDebounceCallback(() =>
    setScrollPosition(window.scrollY - initialContainerTopPosition.current)
  );

  useEffect(() => {
    if (!fixOnScroll) return;

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Container
      {...forwardingProps}
      ref={containerRef}
      _fixOnScroll={fixOnScroll}
      style={{
        transform,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: scale === 0 ? 0 : '100%'
      }}
    >
      {isIframe && (
        <Iframe
          _fixOnScroll={fixOnScroll}
          allow="autoplay"
          src={iframeURL}
          role="presentation"
        />
      )}
      {isVideo && (
        <video
          src={originalValue}
          controls={false}
          autoPlay
          playsInline
          loop
          muted
        />
      )}
    </Container>
  );
}

const getYoutubeURL = (id: string) => {
  const params = new URLSearchParams({
    autoplay: '1',
    loop: '1',
    controls: '0',
    rel: '0',
    mute: '1',
    playlist: `${id}`
  });

  return `https://www.youtube.com/embed/${id}?${params}`;
};

const getVimeoURL = (id: string) =>
  `https://player.vimeo.com/video/${id}?background=1`;

const Container = styled.div<{ _fixOnScroll?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  ${({ _fixOnScroll }) =>
    _fixOnScroll
      ? css`
          position: relative;
          overflow: hidden;
          max-height: 100vh;
        `
      : css`
          right: 0;
          bottom: 0;
        `}
  width: 100%;
  z-index: 1;
`;
const Iframe = styled.iframe<{ _fixOnScroll?: boolean }>`
  border: none;
  width: 100%;
  height: 100%;

  ${({ _fixOnScroll }) =>
    _fixOnScroll &&
    css`
      position: absolute;
      top: 0;
      left: 0;
    `}
`;
